import { type Kopecks, kopecks } from '@glory/kernel';

const compareIds = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Причина правки часов при подтверждении — до 500 символов. */
export const CONFIRM_REASON_MAX = 500;

/**
 * Сумма подтверждения (раздел 5.4): `round(minutes × ratePerHour / 60)` до копейки, половина — вверх.
 * Ставка — копейки за час на дату подтверждения; ставки нет — 0.
 */
export function confirmationAmount(minutes: number, ratePerHour: Kopecks | null): Kopecks {
  if (ratePerHour === null || minutes <= 0) return kopecks(0);
  // Целыми числами: минуты × копейки делятся на 60 с округлением половины вверх, без плавающей точки.
  return kopecks(Math.floor((minutes * ratePerHour * 2 + 60) / 120));
}

/** Часы человека по задаче в окне подтверждения: сколько залогировано и что подтверждает руководитель. */
export interface ConfirmLine {
  readonly loggedMinutes: number;
  readonly confirmedMinutes: number;
  readonly reason: string | null;
}

export type ConfirmIssue = 'minutes' | 'reason_required' | 'reason_too_long';

/**
 * Проверка строки подтверждения: подтверждённые минуты — целое от 0 до 60 000 (`minutes`); если они отличаются
 * от залогированных — причина обязательна (непустая после обрезки, `reason_required`); причина не длиннее
 * `CONFIRM_REASON_MAX` (`reason_too_long`). Ноль часов подтвердить можно. Проблем нет — пусто.
 */
export function confirmIssues(line: ConfirmLine): ConfirmIssue[] {
  const issues: ConfirmIssue[] = [];
  const value = line.confirmedMinutes;
  if (!Number.isInteger(value) || value < 0 || value > 60_000) issues.push('minutes');
  const reason = line.reason?.trim() ?? '';
  if (value !== line.loggedMinutes && reason === '') issues.push('reason_required');
  if (reason.length > CONFIRM_REASON_MAX) issues.push('reason_too_long');
  return issues;
}

/** Подтверждение часов, как оно лежит в `projects.confirmations`. */
export interface ConfirmationRecord {
  readonly id: string;
  readonly taskId: string;
  readonly userId: string;
  readonly confirmedMinutes: number;
  readonly amount: Kopecks;
  /** Когда подтверждено: из нескольких подтверждений человека по задаче последнее — по этому времени. */
  readonly at: Date;
  /** После возврата и повторного подтверждения действует последнее; прежние указывают на него. */
  readonly supersededBy: string | null;
  /** Подписанный акт о выплате, в который попало подтверждение. */
  readonly payoutDocumentId: string | null;
}

/** Что ещё причитается человеку по задаче: подтверждение и разница с уже выплаченным. */
export interface PayableItem {
  readonly confirmationId: string;
  readonly taskId: string;
  readonly userId: string;
  readonly minutes: number;
  readonly amount: Kopecks;
}

/**
 * «К выплате» (раздел 5.4) из подтверждений одного или нескольких людей:
 * - действует только последнее подтверждение человека по задаче (`supersededBy = null`); задача сейчас не
 *   «Готово» (`doneTasks` её не содержит) — по ней ничего не причитается, пока её не подтвердят снова;
 * - уже выплачено по задаче — минуты и сумма последнего (по `at`) подтверждения этого человека с
 *   `payoutDocumentId`; действующее подтверждение, само попавшее в акт, — ничего не причитается;
 * - причитается разница действующего и выплаченного; строки с суммой ≤ 0 не попадают;
 * - подтверждения из `reserved` (уже в акте, который ждёт подписи) не попадают.
 * Порядок — по `at` действующего подтверждения, затем по `id`.
 */
export function payableItems(
  records: readonly ConfirmationRecord[],
  doneTasks: ReadonlySet<string>,
  reserved: ReadonlySet<string> = new Set(),
): PayableItem[] {
  const items: (PayableItem & { readonly at: number })[] = [];
  // Без `Map.groupBy`: домен собирается и для браузера, где ES2024 ещё нет (Safari 16.4).
  const groups = new Map<string, ConfirmationRecord[]>();
  for (const record of records) {
    const key = `${record.taskId}:${record.userId}`;
    groups.set(key, [...(groups.get(key) ?? []), record]);
  }
  for (const group of groups.values()) {
    const current = group.find((record) => record.supersededBy === null);
    if (current === undefined || !doneTasks.has(current.taskId)) continue;
    if (current.payoutDocumentId !== null || reserved.has(current.id)) continue;
    const paid = group
      .filter((record) => record.payoutDocumentId !== null)
      .sort((a, b) => b.at.getTime() - a.at.getTime() || compareIds(b.id, a.id))[0];
    const amount = current.amount - (paid?.amount ?? 0);
    if (amount <= 0) continue;
    items.push({
      confirmationId: current.id,
      taskId: current.taskId,
      userId: current.userId,
      minutes: Math.max(0, current.confirmedMinutes - (paid?.confirmedMinutes ?? 0)),
      amount: kopecks(amount),
      at: current.at.getTime(),
    });
  }
  return items
    .sort((a, b) => a.at - b.at || compareIds(a.confirmationId, b.confirmationId))
    .map(({ at: _at, ...item }) => item);
}

/** Строка акта о выплате из подтверждения: название, минуты, сумма и ссылка на подтверждение. */
export interface PayoutLineDraft {
  readonly confirmationId: string | null;
  readonly title: string;
  readonly minutes: number | null;
  readonly amount: Kopecks;
}

/**
 * Строки акта о выплате из причитающегося (раздел 5.4): по строке на подтверждение, без повторов по
 * `confirmationId` (повтор — пропускается), название — `titleOf(item)`; порядок сохраняется. Итог акта — сумма
 * строк (`payoutTotal` документов), и он равен сумме `amount` уникальных подтверждений.
 */
export function payoutLinesOf(
  items: readonly PayableItem[],
  titleOf: (item: PayableItem) => string,
): PayoutLineDraft[] {
  const seen = new Set<string>();
  const lines: PayoutLineDraft[] = [];
  for (const item of items) {
    if (seen.has(item.confirmationId)) continue;
    seen.add(item.confirmationId);
    lines.push({
      confirmationId: item.confirmationId,
      title: titleOf(item),
      minutes: item.minutes,
      amount: item.amount,
    });
  }
  return lines;
}
